/**
 * Feedback length limits — the single source of truth (#7520).
 *
 * ## Why this module exists
 *
 * The caps used to be four independent literals: `MIN_MESSAGE_LENGTH` /
 * `MAX_MESSAGE_LENGTH` in `src/mcp/tools/feedback/feedback-submit.ts`, a second
 * independent copy of the same two names in `src/app/actions/feedback.ts`, a
 * bare `.max(500)` on the MCP `context` field, and a hardcoded `/2000` counter
 * in `src/components/feedback-modal.tsx`. Nothing tied them together, so the
 * client could keep refusing at 2,000 characters after the server started
 * accepting 10,000 — the drift #7520 exists to remove. Every surface now
 * imports from here.
 *
 * ## Deliberately dependency-free
 *
 * `feedback-modal.tsx` is a client component, so this module must stay pure:
 * no `server-only`, no Prisma, no Zod, no logger. That is also what lets the
 * MCP tool build its Zod error callbacks out of `formatFeedbackTooLongError`
 * without either side importing the other.
 *
 * ## Why the cap is finite
 *
 * 10,000 characters plus the `generateIssueBody` template wrapper stays well
 * inside GitHub's ~65,536-character issue-body limit, and a finite ceiling
 * bounds abuse. It is a raise, not a removal.
 *
 * ## Composition with the intake safety screen (#7521)
 *
 * `classifyFeedbackSafety` screens the WHOLE message — `buildClassifierUserPrompt`
 * interpolates it without truncation and the 400-token cap there bounds the
 * model's OUTPUT, not its input. So raising this ceiling widens what the screen
 * reads rather than what it misses. Anything added here that truncates before
 * the screen would turn the tail of a submission into unscreened text that
 * still reaches the issue body; do not.
 *
 * The screen runs for every org since #16723 retired the per-org flag that
 * shipped it dark, so the raised ceiling is screened text everywhere.
 */

/** Minimum message length. Unchanged by #7520. */
export const FEEDBACK_MIN_MESSAGE_LENGTH = 10

/** Maximum message length. Raised from 2,000 by #7520. */
export const FEEDBACK_MAX_MESSAGE_LENGTH = 10_000

/**
 * Maximum length of the MCP `context` field. Raised from 500 by #7520, where
 * it was a bare literal with neither a name nor a custom error message.
 *
 * The in-app path has no `context` field — it carries richer structured
 * context (`screenshotUrl`, `attachmentIds`, `captureMetadata`) instead — so
 * this bound applies to the MCP surface only. On that path `context` is
 * concatenated into `message` for storage, which is why the two caps are
 * separate numbers rather than one shared budget.
 */
export const FEEDBACK_MAX_CONTEXT_LENGTH = 2_000

/**
 * Maximum length of any comment on a feedback thread (#13578).
 *
 * Covers both a facilitator's triage note — the reason on a Reject and the
 * text of an Annotate — and an ordinary reply, because all three become the
 * same `FeedbackComment` row and a triage note must not be able to exceed
 * what the thread's own reply box accepts.
 *
 * Until #13578 that "matches the reply box" property was a docstring claim
 * over two independent `MAX_COMMENT_LENGTH = 2000` literals, in
 * `feedback-comment-form.tsx` and `feedback-user.ts`. Both now alias this
 * constant, which is the whole reason the module exists (#7520). The other
 * `MAX_COMMENT_LENGTH` literals in the repo — survey-response comments, the
 * MCP `add-comment` tool, `@/lib/comment-constants` — are different domains
 * and deliberately untouched.
 *
 * It lives HERE rather than beside `triageFeedback` because
 * `src/app/actions/feedback-triage.ts` carries `'use server'`, and
 * `check:invariants` (#6238) forbids a non-function export from such a module
 * — Next.js/Turbopack rejects one at build time. This module is already the
 * dependency-free home every feedback surface reads its caps from.
 */
export const FEEDBACK_TRIAGE_NOTE_MAX_LENGTH = 2_000

/**
 * Upper bound on the `clusterSize` a pin-open event may report (#13578).
 *
 * `trackFeedbackPinOpened` is called from the browser, so its input is a
 * claim: without a ceiling any authenticated member can write an arbitrary
 * number into the adoption metric the event exists to produce. The bound is
 * generous rather than tight — clusters form from pins within 24px of each
 * other, so a real one is a handful — because the purpose is to keep a
 * forged value out of the metric, not to second-guess a dense page.
 */
export const MAX_PIN_CLUSTER_SIZE = 100

/** The fields whose lengths are reported back to a submitter. */
export type FeedbackLengthField = 'Message' | 'Context'

/**
 * Format a character count for display: `5142` -> `5,142`.
 *
 * The locale is pinned so the string is identical in every environment — a
 * host-default locale would make the error text (and its tests) depend on
 * where the process happens to run.
 */
export function formatFeedbackCount(value: number): string {
  return value.toLocaleString('en-US')
}

/**
 * The over-limit error, echoing the ACTUAL length alongside the limit.
 *
 * The reported bug was five retry cycles spent guessing how much to cut from a
 * PRD-class submission, because the old message ("Message must be less than
 * 2000 characters", or Zod's bare default for `context`) never said how long
 * the submission actually was. Both intake paths render this one function.
 */
export function formatFeedbackTooLongError(
  field: FeedbackLengthField,
  actualLength: number,
  limit: number,
): string {
  return `${field} is ${formatFeedbackCount(actualLength)} characters; the maximum is ${formatFeedbackCount(limit)}.`
}

/**
 * The over-limit error WITHOUT a count, for the one caller that cannot
 * establish the actual length (Code Review #1 on PR #12981, finding 5).
 *
 * A count-aware message is the point of this module, so this exists only so
 * that a caller with no length in hand says nothing about the length instead of
 * inventing a plausible number.
 */
export function formatFeedbackOverLimitError(
  field: FeedbackLengthField,
  limit: number,
): string {
  return `${field} exceeds the maximum of ${formatFeedbackCount(limit)} characters.`
}

/**
 * The below-minimum error.
 *
 * Wording is unchanged from both pre-#7520 copies: the minimum is 10, so a
 * submitter can already see the whole shortfall, and the string is asserted by
 * existing intake tests. That is also why it ends without a full stop while the
 * over-limit message above ends with one — the difference is preserved
 * compatibility, not an oversight (Code Review #1, finding 10).
 */
export function formatFeedbackTooShortError(): string {
  return `Message must be at least ${FEEDBACK_MIN_MESSAGE_LENGTH} characters`
}

/**
 * Validate a message against both bounds, returning the error text or `null`.
 *
 * Measured on the TRIMMED length, matching what both intake paths persist, so
 * padding can neither buy budget nor spend it. The MCP schema reaches the same
 * measurement by chaining `.trim()` ahead of its bounds — see its header for
 * why that ordering is load-bearing rather than incidental.
 *
 * Shared by `validateFeedback` in `src/app/actions/feedback.ts`; the MCP path
 * expresses the same bounds through its exported Zod schema (which the #3084
 * middleware validation registry also consumes) and reuses the two formatters
 * above for its messages.
 */
export function checkFeedbackMessageLength(message: string): string | null {
  const length = message.trim().length

  if (length < FEEDBACK_MIN_MESSAGE_LENGTH) {
    return formatFeedbackTooShortError()
  }

  if (length > FEEDBACK_MAX_MESSAGE_LENGTH) {
    return formatFeedbackTooLongError(
      'Message',
      length,
      FEEDBACK_MAX_MESSAGE_LENGTH,
    )
  }

  return null
}
